import { useEffect, useRef } from "react";

// How much of the block must be in view, and for how long, to count as read.
// ponytail: a block taller than the panel never reaches this share, so it stays
// marked. Lower the ratio if reports grow blocks that tall.
const VISIBLE_RATIO = 0.6;
const DWELL_MS = 1100;

/**
 * Calls `onSeen` once the element has stayed mostly in view for a moment, so a
 * scroll past it does not count. Attach the returned ref to the element.
 */
export function useSeenWhenVisible(isWatching: boolean, onSeen: () => void) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!isWatching || !element) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(timer);
        if (entry.intersectionRatio >= VISIBLE_RATIO) {
          timer = setTimeout(onSeen, DWELL_MS);
        }
      },
      { threshold: VISIBLE_RATIO }
    );
    observer.observe(element);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [isWatching, onSeen]);

  return ref;
}
